export const isListItemOption = <T extends InputSingleValue>(
  item?: ListItemOptionOrPlainValue<T>,
): item is ListItemOption<T> => {
  return item !== null && typeof item === "object" && "value" in item && "label" in item;
};

export const getListItemOptionLabel = <T extends InputSingleValue>(item?: ListItemOptionOrPlainValue<T>): string => {
  if (isListItemOption(item)) {
    return item.label;
  }
  return item as string;
};

export const translateListItemOption = <T extends InputSingleValue>(
  item?: ListItemOptionOrPlainValue<T>,
  options?: I18nFormatOptions,
): ListItemOptionOrPlainValue<T> => {
  const tryTranslate = (label?: string) => (label && options?.te(label) ? options.t(label) : (label ?? ""));

  if (isListItemOption(item)) {
    return {
      ...item,
      label: tryTranslate(item.label),
    };
  }

  if (typeof item === "string") {
    return tryTranslate(item) as ListItemOptionOrPlainValue<T>;
  }
  return item as ListItemOptionOrPlainValue<T>;
};

export const getListItemOptionValue = <T extends InputSingleValue>(item?: ListItemOptionOrPlainValue<T>) => {
  if (isListItemOption(item)) {
    return item.value;
  }
  return item;
};

export const isListItemOptionDisabled = <T extends InputSingleValue>(item?: ListItemOptionOrPlainValue<T>) => {
  if (isListItemOption(item)) {
    return item.disabled;
  }
  return false;
};

export const compareListItemValue = <T extends InputSingleValue>(item: ListItemOptionOrPlainValue<T>, value: T) => {
  return $ck._.isEqual(getListItemOptionValue(item), value);
};

export const findListItemByValue = <T extends InputSingleValue>(
  items: ListItemOptionOrPlainValue<T>[],
  value: T,
): ListItemOptionOrPlainValue<T> | undefined => {
  return items.find((item) => compareListItemValue(item, value));
};

export const findListItem = <T extends InputSingleValue>(
  items: ListItemOptionOrPlainValue<T>[],
  itemOrValueToSearch: ListItemOptionOrPlainValue<T>,
  compareFn: typeof compareListItemValue = compareListItemValue,
): { index: number; item?: ListItemOptionOrPlainValue<T> | undefined } => {
  const value = getListItemOptionValue(itemOrValueToSearch);
  const index = items.findIndex((item) => compareFn(item, value));
  return {
    index,
    item: items[index],
  };
};
